import { ProtocolAmount, SettlementDebtBreakdown, SettlementProtocolBreakdown } from '../../../models/Entities/Settlements/SettlementProtocolBreakdown';

/** Lógica del desglose por protocolo, sin React. */

interface SettlementLike {
    practitioner?: { practitionerId?: string | null }[] | null;
    protocol?: { protocolId?: number | null }[] | null;
}

/**
 * El desglose sólo aporta cuando la liquidación puede mezclar protocolos: la
 * de un médico (junta todo lo que hizo en el período) o una que tenga más de
 * un protocolo asociado. Una liquidación protocolar común es de un solo
 * protocolo y el desglose repetiría el total.
 */
export const shouldShowBreakdown = (settlement: SettlementLike | null | undefined): boolean => {
    if (!settlement) return false;
    if ((settlement.practitioner?.length ?? 0) > 0) return true;
    return (settlement.protocol?.length ?? 0) > 1;
};

export type ProtocolTitleLookup = (protocolId: number) => string | undefined;

const noTitle: ProtocolTitleLookup = () => undefined;

/** Título del protocolo desde el store; el `protocolName` del API es su número y queda de respaldo. */
export const protocolLabel = (item: { protocolId?: number | null; protocolName?: string | null }, titleOf: ProtocolTitleLookup = noTitle): string => {
    const title = item.protocolId ? (titleOf(item.protocolId) || '').trim() : '';
    if (title) return title;
    const name = (item.protocolName || '').trim();
    if (name) return name;
    return item.protocolId ? `Protocolo #${item.protocolId}` : 'Sin protocolo';
};

/** Más peso primero; a igual peso, por nombre, para que el orden no baile entre recargas. */
export const sortProtocolAmounts = (amounts: ProtocolAmount[] | null | undefined, titleOf: ProtocolTitleLookup = noTitle): ProtocolAmount[] =>
    [...(amounts ?? [])].sort((a, b) =>
        (b.percentage - a.percentage) || protocolLabel(a, titleOf).localeCompare(protocolLabel(b, titleOf), undefined, { sensitivity: 'base', numeric: true })
    );

export interface CurrencyNet {
    currency: string;
    net: number;
    withholding: number;
    owner: number;
}

/** Totales por moneda de lo que reparte el desglose, para el pie de la tarjeta. */
export const netByCurrency = (amounts: ProtocolAmount[] | null | undefined): CurrencyNet[] => {
    const totals = new Map<string, CurrencyNet>();
    (amounts ?? []).forEach((item) => {
        const currency = (item.currencyCode || '').toUpperCase();
        const current = totals.get(currency) ?? { currency, net: 0, withholding: 0, owner: 0 };
        current.net += item.netAmount || 0;
        current.withholding += item.withholding || 0;
        current.owner += item.ownerAmount || 0;
        totals.set(currency, current);
    });
    return Array.from(totals.values()).sort((a, b) => a.currency.localeCompare(b.currency));
};

/** Deudas con algo pendiente primero, después las saldadas, cada grupo por fecha descendente. */
export const sortDebts = (debts: SettlementDebtBreakdown[] | null | undefined): SettlementDebtBreakdown[] =>
    [...(debts ?? [])].sort((a, b) => {
        const pendingA = a.pendingAmount > 0 ? 0 : 1;
        const pendingB = b.pendingAmount > 0 ? 0 : 1;
        if (pendingA !== pendingB) return pendingA - pendingB;
        return new Date(b.debtDate).getTime() - new Date(a.debtDate).getTime();
    });

export const hasBreakdownData = (breakdown: SettlementProtocolBreakdown | null | undefined): boolean =>
    !!breakdown && ((breakdown.protocolAmounts?.length ?? 0) > 0 || (breakdown.debts?.length ?? 0) > 0);

export const formatShare = (percentage: number): string =>
    `${(percentage || 0).toLocaleString('es-AR', { maximumFractionDigits: 1 })}%`;
